import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { buildMenuConfig } from '../config/navigation/menuConfig';
import { buildMenuSections } from '../config/navigation/menuSections';
import { CONCEPT_BUILDER_KEY, PRACTICE_TEST_KEY } from '../config/navigation/navigation.constants';
import useBusinessFunctionsNavItems from '../config/navigation/useBusinessFunctionsNavItems';
import useConceptBuilderNavItems from '../config/navigation/useConceptBuilderNavItems';
import { globalpermissions } from '../Services/Permission';
import { userInformationAtom } from '../Services/_states';
import { authzContextAtom } from '../authorization/authzState';
import { filterNavigation } from '../utils/navigation/filterNavigation';
import { isPlatformSuperAdmin } from '../utils/isPlatformSuperAdmin.ts';

/**
 * Resolves navigation tree for AppSidebar with v2 RBAC filtering.
 */
export default function useAppNavigation() {
  const userInfo = useRecoilValue(userInformationAtom);
  const authz = useRecoilValue(authzContextAtom);
  const isSuperuser =
    userInfo?.is_superuser === true || isPlatformSuperAdmin(userInfo);

  const {
    conceptBuilderItem,
    practiceTestItem,
    onConceptBuilderExpand,
  } = useConceptBuilderNavItems({ isSuperuser });

  const { items: businessFunctionsItems } = useBusinessFunctionsNavItems();

  const sections = useMemo(() => {
    const menuConfig = buildMenuConfig();
    const rawSections = buildMenuSections(menuConfig, { businessFunctionsItems });

    const withConceptBuilder = rawSections.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        if (!item) return item;
        if (item.key === CONCEPT_BUILDER_KEY) return conceptBuilderItem || item;
        if (item.key === PRACTICE_TEST_KEY) return practiceTestItem || item;
        return item;
      }),
    }));

    return filterNavigation(withConceptBuilder, authz);
  }, [businessFunctionsItems, conceptBuilderItem, practiceTestItem, authz]);

  return {
    sections,
    userInfo,
    isSuperuser,
    onConceptBuilderExpand,
    profileName: userInfo?.profile?.name || 'User',
    displayName:
      [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(' ') ||
      userInfo?.username ||
      'User',
    organisationName: userInfo?.profile?.organisation?.name || 'Machadalo',
    classListAccessPermission:
      globalpermissions?.performancePermissions?.classListAccess,
  };
}
